<template>
  <div>
    <app-spinner v-if="!lead_info" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">{{ lead_info.name }} <span v-if="lead_info.pax">({{ lead_info.pax }})</span></h1>
          <app-option-picker
            v-if="is_general_manager || is_sales_manager || is_sales_supervisor || is_support"
            :options="lead_info.status.available_statuses"
            v-model="lead_info.status"
            @click="onOptionSelected"
          />
          <div v-if="(is_general_manager || is_sales_supervisor) && lead_info.manager" class="manager-title">
            <div class="manager-title__photo">
              <img :src="getUserAvatar(lead_info.manager.crop.crop_url)" alt="Avatar">
            </div>
            <span class="manager-title__name">{{ lead_info.manager.full_name }}</span>
          </div>
        </app-cells>
        <app-cells :indent="false">
          <router-link
            v-if="can_view_lead_flow"
            :to="{ name: 'crm-task-tracker', query: { lead: String($route.params.id) } }"
            class="btn btn--blue btn--small"
          >
            Show in lead flow
          </router-link>
          <app-button
            v-if="!lead_info.manager && lead_info.status.code !== 'spam'"
            theme="error"
            size="small"
            @click.native="onShowStatusModal('spam')"
          >
            Mark as spam
          </app-button>
          <app-button
            v-if="!lead_info.manager && lead_info.status.code !== 'double_request'"
            theme="info"
            size="small"
            @click.native="onShowStatusModal('double_request')"
          >
            Mark as double request
          </app-button>
          <app-button
            v-if="lead_info.manager || (is_general_manager || is_sales_supervisor)"
            theme="warn"
            size="small"
            @click.native="$modal.show('manager-modal')"
          >
            {{ lead_info.manager ? 'Change manager' : 'Assign manager' }}
          </app-button>
          <a
            v-if="is_superuser"
            :href="`${admin_url}leads/lead/${$route.params.id}/change/`"
            target="_blank"
            class="btn btn--blue btn--small"
          >
            Django
          </a>
        </app-cells>
      </app-cells>
      <div v-if="lead_info.manager && !lead_info.my_lead" class="attention-info">
        <img src="@/assets/img/warning-icon.svg" alt="">
        <p>This lead is managed by {{ lead_info.manager.full_name }}. Please be attentive when taking any actions related to it.</p>
      </div>
      <div v-if="lead_info.status.reason_required && lead_info.status_reason" class="attention-info">
        <img src="@/assets/img/warning-icon.svg" alt="Warning">
        <p>
          <b>Reason:</b> {{ lead_info.status_reason.name }}.
          <span v-if="lead_info.status_reason_note"><b>Additional note:</b> {{ lead_info.status_reason_note }}.</span>
        </p>
      </div>
      <div
        class="lead-overview"
        :class="{ 'lead-overview--with-contactability': contactability }"
      >
        <div class="info-cards lead-overview__cards">
          <div class="info-cards__card">
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Request:</span><span>{{ dateTimeFormat(lead_info.created_at) }}</span>
              </div>
            </div>
            <div class="info-cards__row" v-if="lead_info.analytic_info.assignment_date">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Assigned:</span><span>{{ dateTimeFormat(lead_info.analytic_info.assignment_date) }}</span>
              </div>
            </div>
            <div class="info-cards__row" v-if="lead_info.analytic_info.confirmation_date">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Confirmed:</span><span>{{ dateTimeFormat(lead_info.analytic_info.confirmation_date) }}</span>
              </div>
            </div>
          </div>
          <div v-if="lead_info.client_group" class="info-cards__card">
            <div v-if="is_general_manager || is_sales_manager || is_sales_supervisor || is_support" class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Group:</span>
                <router-link
                  :to="{ name: 'clients-groups-general', params: { id: lead_info.client_group.id } }"
                  class="info-cards__link"
                >
                  {{ lead_info.client_group.name }}
                </router-link>
              </div>
            </div>
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Tour dates:</span>
                <span v-if="lead_info.client_group.tour_dates.first_activity && lead_info.client_group.tour_dates.last_activity">{{ dateFormat(lead_info.client_group.tour_dates.first_activity) }} - {{ dateFormat(lead_info.client_group.tour_dates.last_activity) }}</span>
                <span v-else>-</span>
              </div>
            </div>
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Travel dates:</span>
                <span v-if="lead_info.client_group.transport.arrival && lead_info.client_group.transport.departure">{{ dateFormat(lead_info.client_group.transport.arrival) }} - {{ dateFormat(lead_info.client_group.transport.departure) }}</span>
                <span v-else>-</span>
              </div>
            </div>
          </div>
          <div class="info-cards__card">
            <div class="info-cards__row">
              <div class="info-cards__row-item info-cards__row-item--full">
                <span>Last contact:</span><span>{{ dateTimeFormat(lead_info.last_contact) }}</span>
              </div>
            </div>
          </div>
        </div>
        <contactability
          v-if="contactability"
          :contactability="contactability"
        />
        <app-cells v-if="lead_info.manager" class="lead-overview__note" :indent="false">
          <app-textarea
            class="lead-overview__note-field"
            v-model="lead_info.note"
            rows="3"
            @change.native="onUpdateNotes"
            placeholder="Notes"
          />
        </app-cells>
      </div>
      <app-cells v-if="!lead_info.manager && is_sales_manager">
        <app-button
          @click.native="$modal.show('accept-manager-modal')"
          :disabled="!lead_info.climbing && !lead_info.island && !lead_info.safari && !lead_info.day_trip"
        >
          Assign to me
        </app-button>
      </app-cells>
      <app-cells v-if="lead_info.is_double_request">
        <span class="status-tag" style="background-color: #439ad1">
          This lead is in Double Requests of another manager. Check the chart at the bottom of the page.
        </span>
      </app-cells>
      <app-tabs v-if="lead_info.manager" :tabs="tabs_to_show" />
      <transition name="fade" mode="out-in">
        <router-view @update-info="getInfo" :leadInfo="lead_info" />
      </transition>
    </template>
    <modal
      name="manager-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('manager-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Transfer lead to another manager</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckManagerForm">
            <app-form-group label="Sales manager" required>
              <app-select
                v-model="manager_form.manager"
                :options="managers_options"
                :reduce="item => item.id"
                :display-field="option => `${option.first_name} ${option.last_name}`"
                label="first_name"
                placeholder="Sales manager"
                :error="$v.manager_form.manager.$error"
              />
              <template #error>
                <div v-if="$v.manager_form.manager.$dirty && !$v.manager_form.manager.required">
                  Required field
                </div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="managerSubmit" :disabled="$v.manager_form.$error">Transfer lead</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="reason-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('reason-modal')" class="popup__close" type="button"></button>
        <div class="popup__title"><span>Choose reason</span></div>
        <div class="popup__body">
          <form @submit.prevent="onCheckReasonForm">
            <app-form-group label="Reason" required>
              <app-select
                v-model="reason_form.status_reason"
                :options="reason_options"
                :reduce="item => item.code"
                :clearable="false"
                placeholder="Reason"
                :error="$v.reason_form.status_reason.$error"
              />
              <template #error>
                <div v-if="$v.reason_form.status_reason.$dirty && !$v.reason_form.status_reason.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Additional notes" label-for="reason-notes">
              <app-textarea
                v-model="reason_form.status_reason_note"
                id="reason-notes"
                placeholder="Additional notes"
              />
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="reason_submit" :disabled="$v.reason_form.$error">Save status</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="accept-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Change status
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('accept-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onMarkSpam" ref="submit_spam">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="accept-manager-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-manager-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Assign manager
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('accept-manager-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onAssignManager" ref="assign_manager">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="next-contact-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('next-contact-modal')" class="popup__close" type="button"></button>
        <div class="popup__title"><span>Set next contact</span></div>
        <div class="popup__body">
          <form @submit.prevent="onCheckNextContactForm">
            <app-form-group label="Next contact" required>
              <app-date-picker
                v-model="next_contact_form.next_contact"
                placeholder="Next contact"
                :error="$v.next_contact_form.next_contact.$error"
              />
              <template #error>
                <div v-if="$v.next_contact_form.next_contact.$dirty && !$v.next_contact_form.next_contact.required">
                  Required field
                </div>
                <div v-if="$v.next_contact_form.next_contact.$dirty && !$v.next_contact_form.next_contact.minValue">
                  Date cannot be less than the current date
                </div>
              </template>
            </app-form-group>
            <app-cells :indent="false" position="center">
              <app-button type="submit" ref="next_contact_submit" :disabled="$v.next_contact_form.$error">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { format, parse, parseISO } from 'date-fns'
import { getLead, getLeadContactability, getLeadsStatusReason, getUsers, patchAssignToMe, patchLead } from '@/http'
import AppOptionPicker from '@/components/AppOptionPicker'
import { required } from 'vuelidate/lib/validators'
import AppTabs from '@/components/AppTabs'
import Contactability from './Contactability'
import { normalizeLeadId } from '@/views/crm/task-tracker/leadFlowContext'

export default {
  name: 'LeadsDetailLayout',
  components: { AppOptionPicker, AppTabs, Contactability },
  data() {
    return {
      tabs: [
        { name: 'leads-detail-lead', label: 'Lead', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'marketing_supervisor', 'support'] },
        { name: 'leads-detail-reservations', label: 'Reservations', roles: [] },
        { name: 'leads-detail-invoices', label: 'Authorization forms', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'support'] },
        { name: 'leads-detail-status', label: 'Status history', roles: ['general_manager', 'sales_manager', 'sales_supervisor', 'support', 'marketing_supervisor'] },
        { name: 'leads-detail-advertising', label: 'Advertising', roles: [] },
        { name: 'leads-detail-messages', label: 'Messages', roles: ['general_manager'] },
      ],
      tabs_to_show: [],
      lead_info: null,
      managers_options: [],
      manager_form: {},
      reason_options: [],
      reason_form: {
        status_reason: null,
      },
      old_status: null,
      status_to_change: '',
      next_contact_form: {
        next_contact: null,
      },
      admin_url: process.env.VUE_APP_ADMIN_URL,
      contactability: null,
      loadedLeadId: null,
      leadRequestGeneration: 0,
      contactabilityRequestGeneration: 0,
    }
  },
  validations: {
    manager_form: {
      manager: { required }
    },
    reason_form: {
      status_reason: { required },
    },
    next_contact_form: {
      next_contact: {
        required,
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
    },
  },
  computed: {
    can_view_lead_flow() {
      const roles = ['general_manager', 'marketing_supervisor', 'sales_supervisor', 'lead_new_flow']
      return this.$store.state.userGroups.some(group => roles.includes(group.code))
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_superuser() {
      return this.$store.state.userData.is_superuser
    },
    is_support() {
      return this.$store.state.userGroups.map(item => item.code).includes('support')
    },
    can_view_contactability() {
      const roles = ['general_manager', 'sales_manager', 'sales_supervisor', 'marketing_supervisor']
      return this.$store.state.userGroups.some(group => roles.includes(group.code))
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(leadId) {
        this.loadLead(leadId)
      }
    }
  },
  created() {
    const tabs = []
    this.tabs.forEach(item => {
      if (!item.roles.length) {
        tabs.push(item)
        return
      }
      item.roles.forEach(role => {
        if (this.$store.state.userGroups.some(groupRole => groupRole.code === role)) tabs.push(item)
      })
    })
    this.tabs_to_show = tabs
    getUsers({ group: 'sales_manager', is_active: true }).then(response => { this.managers_options = response.data })
  },
  beforeDestroy() {
    this.leadRequestGeneration += 1
    this.contactabilityRequestGeneration += 1
  },
  methods: {
    async loadLead(value) {
      const leadId = normalizeLeadId(value)
      const generation = this.leadRequestGeneration + 1
      this.leadRequestGeneration = generation
      this.contactabilityRequestGeneration += 1
      if (leadId !== this.loadedLeadId) {
        this.lead_info = null
        this.contactability = null
      }
      if (!leadId) {
        this.loadedLeadId = null
        return
      }

      try {
        const { data } = await getLead(leadId)
        if (generation !== this.leadRequestGeneration || leadId !== normalizeLeadId(this.$route.params.id)) return
        this.lead_info = data
        this.loadedLeadId = leadId
        this.old_status = data.status
        document.title = `${data.name} ${data.pax ? `(${data.pax})` : ''} | Altezza Board`
        this.getContactability(leadId)
      } catch (_) {}
    },
    getInfo() {
      return this.loadLead(this.$route.params.id)
    },
    async getContactability(value) {
      const leadId = normalizeLeadId(value)
      const generation = this.contactabilityRequestGeneration + 1
      this.contactabilityRequestGeneration = generation
      this.contactability = null
      if (!this.can_view_contactability || !leadId) return

      try {
        const { data } = await getLeadContactability(leadId)
        if (generation !== this.contactabilityRequestGeneration || leadId !== normalizeLeadId(this.$route.params.id)) return
        this.contactability = data
      } catch (_) {
        if (generation === this.contactabilityRequestGeneration && leadId === normalizeLeadId(this.$route.params.id)) {
          this.contactability = null
        }
      }
    },
    onShowStatusModal(status) {
      this.status_to_change = status
      this.$modal.show('accept-modal')
    },
    onMarkSpam() {
      this.$refs.submit_spam.preload = true
      patchLead(this.$route.params.id, { status: this.status_to_change })
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.$modal.hide('accept-modal')
        })
        .catch(() => { this.$refs.submit_spam.preload = false })
    },
    onUpdateNotes() {
      patchLead(this.$route.params.id, { note: this.lead_info.note })
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
        })
    },
    onAssignManager() {
      this.$refs.assign_manager.preload = true
      patchAssignToMe(this.$route.params.id)
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Manager assigned',
          })
          this.$modal.hide('accept-manager-modal')
        })
        .catch(() => { this.$refs.assign_manager.preload = false })
    },
    onShowModalReason() {
      this.reason_form.status_reason = null
      this.reason_form.status_reason_note = null
      this.$v.reason_form.$reset()
      this.$modal.show('reason-modal')
    },
    onCheckReasonForm() {
      this.$v.reason_form.$touch()
      if (this.$v.reason_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendReasonForm()
      }
    },
    sendReasonForm() {
      this.$refs.reason_submit.preload = true
      patchLead(this.$route.params.id, this.reason_form)
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.$modal.hide('reason-modal')
        })
        .catch(() => {
          this.$refs.reason_submit.preload = false
        })
    },
    onShowModalNextContact() {
      this.next_contact_form.next_contact = null
      this.$v.next_contact_form.$reset()
      this.$modal.show('next-contact-modal')
    },
    onCheckNextContactForm() {
      this.$v.next_contact_form.$touch()
      if (this.$v.next_contact_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendNextContactForm()
      }
    },
    sendNextContactForm() {
      this.$refs.next_contact_submit.preload = true
      patchLead(this.$route.params.id, this.next_contact_form)
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
          this.$modal.hide('next-contact-modal')
        })
        .catch(() => {
          this.$refs.next_contact_submit.preload = false
        })
    },
    onOptionSelected(e) {
      if (e.reason_required) {
        this.lead_info.status = this.old_status
        getLeadsStatusReason({ status: e.code, active: true })
          .then(response => { this.reason_options = response.data })
        this.reason_form.status = e.code
        this.onShowModalReason()
        return
      }
      if (e.next_contact) {
        this.lead_info.status = this.old_status
        this.next_contact_form.status = e.code
        this.onShowModalNextContact()
        return
      }
      patchLead(this.$route.params.id, {status: this.lead_info.status.code})
        .then(() => {
          this.getInfo()
          this.$notify({
            type: 'success',
            title: 'Status changed',
          })
        })
        .catch(() => {
          this.lead_info.status = this.old_status
        })
    },
    onCheckManagerForm() {
      this.$v.manager_form.$touch()
      if (this.$v.manager_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.$refs.managerSubmit.preload = true
        patchLead(this.$route.params.id, { manager: this.manager_form.manager })
          .then(() => {
            this.$emit('update-info')
            this.$notify({
              type: 'success',
              title: 'Data saved',
            })
            this.$modal.hide('manager-modal')
            this.manager_form = {}
            this.$v.manager_form.$reset()
          })
          .catch(() => { this.$refs.managerSubmit.preload = false })
      }
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
  }
}
</script>

<style lang="sass" scoped>
.lead-overview
  margin: 30px 0

.lead-overview--with-contactability
  display: grid
  grid-template-areas: "cards contactability" "note contactability"
  grid-template-columns: minmax(0, 1fr) minmax(500px, 540px)
  gap: 20px
  align-items: start

.lead-overview__cards
  grid-area: cards
  margin: 0

  .info-cards__card
    flex: 1 1 0
    max-width: none
    min-width: 0

.lead-overview__note
  grid-area: note
  width: 100%

.lead-overview__note-field
  min-height: 70px

.lead-overview
  > .contactability
    grid-area: contactability
    width: 100%

@media(max-width: 1200px)
  .lead-overview--with-contactability
    display: flex
    flex-direction: column

  .lead-overview
    > .contactability
      order: 2
      max-width: 100%
      margin-top: 20px

  .lead-overview__note
    order: 3
    margin-top: 20px

@media(max-width: 760px)
  .lead-overview__cards
    flex-direction: column

    .info-cards__card
      width: 100%

  .lead-overview
    > .contactability
      width: 100%
</style>
